import type {
  Block, Cell, ChipCell, Column, MtxBlock, MtxCell, MtxRow, NoteBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinor, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Providers & Routing — the register, the capability matrix, health, the
 * institution's invoices and the rail catalogue (go-wallet#757, #771, #815,
 * #845, #716; this console #144).
 *
 *   pv-registry   GET /v1/admin/providers
 *   pv-caps       GET /v1/admin/providers  →  /{key}/capabilities per institution
 *   pv-health     GET /v1/admin/providers  →  /{key}/health per institution
 *                 GET /v1/admin/providers/{key}/health/observations  (row opener)
 *   pv-invoices   GET /v1/admin/provider-invoices
 *   pv-methods    GET /v1/admin/payment-methods
 *
 * # `pv-health` is law 19, and the corpus draws it wrong
 *
 * Law 19: provider and worker health is never framed as an outage. A degraded
 * rail is a ROUTING INPUT — stale reporting on the affected view — never a
 * service-down state and never a reason to block a money screen. The corpus
 * draws `degraded` in the critical tone, which is exactly the reading the law
 * forbids, so nothing on that screen is red. Red is kept for a read that
 * FAILED, which is a different claim entirely: the console not knowing, rather
 * than the institution being down.
 *
 * `unknown` is three facts and the reason code separates them — nothing was
 * ever reported, something was and its own expiry has passed, or the reporter's
 * clock is ahead of ours. A missing integration, a stopped worker and a wrong
 * clock have three different remedies and one word collapses all three.
 * `observed` is read as well, because an unparseable cached status also
 * resolves to `unobserved` on a row that HAS been observed.
 *
 * Five of that screen's eight columns are dropped and the backend refuses each
 * by name: there is no per-rail posture (the observations table is keyed by
 * provider ALONE, and attributing an institution's outage to one rail is
 * backwards for a routing decision), no latency (a Prometheus gauge, not a
 * Postgres fact), no success rate (nothing writes `error_rate` observations, so
 * a computed rate would render `0% errors` where the truth is "never
 * measured"), and no routing effect (what routing DID is
 * `payment_route_candidates`, a different fact — conflating them makes a
 * routing bug look like an outage).
 *
 * # `false` on `pv-caps` means "not confirmed in writing", never "cannot"
 *
 * Those are different claims about a third party and the second is defamatory
 * in a compliance file if the institution does in fact offer it. The port
 * collapses "confirmed false" and "unconfirmed" on purpose — a third state
 * would be somewhere for an assumption to live — so the distinction is carried
 * in words on every cell here.
 *
 * There is no `provider_capabilities` table and adding one would be the bug:
 * these are compile-time booleans read from the same adapter the money path
 * holds, and a table would make them editable. An operator who could tick
 * `per_customer_iban` would answer by UI the question invariant 1 turns on.
 * So there is no control on that screen at any permission.
 *
 * A registered institution can 404 on the capability route, and that is a
 * STATE rather than a failure: a deployment not configured with an institution
 * has no capabilities to report, and a row of falses would read as a statement
 * about our integration with it.
 *
 * # `pv-invoices` — an unreconciled invoice has no delta, and its delta is not zero
 *
 * `reconciled: false` means NOBODY HAS RUN THE COMPARISON and the whole
 * `reconciliation` block is absent. A `0` in the delta column would report
 * "this month tied to the cent" about a document nobody has looked at.
 *
 * And a zero delta is not agreement either: branch on `outcome`, because a
 * comparison whose money and counts both match is still `discrepant` while any
 * settled payment in the period carries no pinned cost. Acknowledgement settles
 * nothing — a case leaves the open set by being posted or resolved, never by an
 * operator recording that they saw it.
 *
 * # No control on any of these five
 *
 * Every route is a read. The registry's designed ACTIVATION control has no
 * endpoint at all — `ProviderAdmin.Activate` and `Deactivate` are tested and
 * have no caller — so it is not drawn, disabled or otherwise: a control with no
 * route behind it is fabricated capability (go-wallet#862). Health is observed
 * and never asserted. A capability changes by a code change and a redeploy.
 *
 * # `pv-routing` is absent, and it is the only one left
 *
 * `pv-routing` has NO read. `routing_pins` exists and `RoutingPinStore` is
 * constructed in `cmd/walletd` for the payment path only; no admin route serves
 * a pin or a resolution trace.
 *
 * `pv-offerings` was deferred from here to the session that owned the versioned
 * offerings and buy-price family, and that change is `offerings.ts` (#178). It
 * lives in its own module rather than this one for the reason the deferral
 * gave: the buy price sits behind `offering.buy_price.read` on a per-key path
 * rather than on the list, because "a permission enforced by a handler
 * stripping fields out of a shared response is one somebody eventually forgets
 * to strip" — so the tariff is a SECOND read on a row opener, and folding it
 * into this module's shared reading of the register would be the first step
 * towards folding it into the register itself.
 */

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

export const PROVIDERS_PATH = '/providers'
export const PROVIDER_INVOICES_PATH = '/provider-invoices'
export const PAYMENT_METHODS_PATH = '/payment-methods'

export function providerCapabilitiesPath(key: string): string {
  return `${PROVIDERS_PATH}/${encodeURIComponent(key)}/capabilities`
}

export function providerHealthPath(key: string): string {
  return `${PROVIDERS_PATH}/${encodeURIComponent(key)}/health`
}

export function providerObservationsPath(key: string): string {
  return `${providerHealthPath(key)}/observations`
}

const REGISTER_SOURCE = 'postgres · provider_registry (GET /v1/admin/providers)'
const CAPS_SOURCE = 'the adapter the money path holds (GET /v1/admin/providers/{key}/capabilities)'
const HEALTH_SOURCE = 'postgres · provider_health_observations (GET /v1/admin/providers/{key}/health)'
const INVOICES_SOURCE = 'postgres · provider_invoices (GET /v1/admin/provider-invoices)'
const METHODS_SOURCE = 'postgres · payment_methods (GET /v1/admin/payment-methods)'

/**
 * Who these figures answer to.
 *
 * A licence, an authority and a registration reference are claims about a
 * LICENSED THIRD PARTY that somebody here may be asked to justify — not our own
 * operational record — so the badge says so rather than letting `operational`
 * imply we are answerable for the institution's regulatory status.
 */
const REGISTER_AUTHORITY =
  'operational — the licence and registration are the institution’s own, recorded by us'
/** Observed by our probe. Not the institution's statement about itself. */
const HEALTH_AUTHORITY = 'operational — observed by our own probe, never asserted by anyone'
/**
 * Two sides, and only one is ours. `accrued` is our arithmetic over pinned
 * costs; `invoiced` is the institution's own billing document. The delta
 * between them is the whole screen, and a badge naming one authority for both
 * would hide which side a discrepancy is arguable against.
 */
const INVOICE_AUTHORITY =
  'operational for the accrual; the institution’s own document for the invoiced figure'
const CAPS_AUTHORITY =
  'compile-time — true only where the institution confirmed it in writing'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * `word in TABLE` walks the prototype chain, so `toString` and `__proto__`
 * answer true against any object literal and the lookup returns a FUNCTION
 * rather than a tone. An unrecognised status would come back "recognised",
 * record no gap, and leave the screen badging itself live over a word it does
 * not know.
 */
function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

function readInt(o: Record<string, unknown>, key: string): IntRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * The epoch guard is not paranoia: several of these are plain `time.Time` with
 * no `omitempty`, so a zero value reaches the wire as `0001-01-01T00:00:00Z`
 * and parses into a date in the year 1 — which renders as a timestamp rather
 * than as an absence.
 */
function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/** Just the date. A period boundary is a day, and the time on it is noise. */
function dayOf(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '') return null
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return null
  return at.toISOString().slice(0, 10)
}

/** No ambient clock. Truncated, never rounded up. */
function spanOf(seconds: number): string {
  const s = Math.trunc(seconds)
  if (s < 60) return `${String(s)}s`
  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${String(minutes)}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${String(hours)}h ${String(minutes % 60).padStart(2, '0')}m`
  return `${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`
}

/**
 * An amount cell, or a stated absence. Never a bare number — law 8.
 *
 * `exactMinor` reads both the quoted-decimal form `minorUnits` marshals and a
 * bare JSON integer, and refuses anything past 2^53-1 where `JSON.parse` has
 * already dropped digits.
 */
function moneyCell(o: Record<string, unknown>, key: string): Cell {
  const m = asRecord(o[key])
  if (m === null) return '— not stated'
  const minor = exactMinor(m['amount_minor'])
  if (minor === null) return '— not a figure this console can state'
  const currency = m['currency']
  if (!isCurrencyCode(currency)) return '— no currency on this amount'
  return [minor, currency.trim().toUpperCase()]
}

function moneyReadable(o: Record<string, unknown>, key: string): boolean {
  const m = asRecord(o[key])
  if (m === null) return false
  return exactMinor(m['amount_minor']) !== null && isCurrencyCode(m['currency'])
}

/** Groups repeated gaps so the provenance chip stays readable at many rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

/**
 * The backend's own disclosure sentence, as the backend wrote it.
 *
 * Same helper and the same argument as `./onboarding.ts`: these responses carry
 * required `*_note` fields written by the handler that knows what its own
 * schema cannot say, and they are quoted rather than paraphrased because a
 * paraphrase turns a permanent property of the system into something that reads
 * as a temporary gap.
 *
 * They cannot be rendered whole. `withoutRoadmapNotes` drops any note body over
 * 220 characters at `readScreen` (#120), and every one of these is several
 * times that, so a verbatim block would render NOTHING — worse than an extract,
 * because the operator is then told nothing at all. What is rendered is the
 * field's own FIRST SENTENCE, verbatim, with the field named in `ref`. These
 * handlers front-load the load-bearing clause, so the first sentence is the one
 * that changes what somebody does.
 *
 * Nothing is emitted when the field is absent: this console must never state a
 * sentence of its own as the backend's.
 */
function disclosure(
  o: Record<string, unknown>, key: string, kicker: string, title: string, tone: Tone,
): NoteBlock | null {
  const full = readText(o, key)
  if (full === null) return null
  const match = /^[\s\S]*?[.](?=\s|$)/.exec(full.trim())
  let body = (match === null ? full.trim() : match[0]).trim()
  if (body.length > MAX_DISCLOSURE) {
    const cut = body.slice(0, MAX_DISCLOSURE).lastIndexOf(' ')
    body = `${body.slice(0, cut > 0 ? cut : MAX_DISCLOSURE).trimEnd()}…`
  }
  return { t: 'note', tone, kicker, title, body, ref: `${key}, as the backend wrote it` }
}

const MAX_DISCLOSURE = 210

// ---------------------------------------------------------------------------
// The read, resolved into an answer either way
// ---------------------------------------------------------------------------

export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      readonly decisionRef: string | null
      readonly refused: boolean
      /** 404 is a STATE on the capability route, not a failure. See `pv-caps`. */
      readonly status: number | null
    }

async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
        status: error.status,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      ok: false, reason: `${subject} could not be reached.`,
      decisionRef: null, refused: false, status: null,
    }
  }
}

/** Law 13 — a refusal with no reference says so rather than inventing one. */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  return {
    blocks: [{
      t: 'note', tone: 'r', kicker, title,
      body: `${failed.reason} ${body}`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

/** Neither route states when it read its rows, so the stamp is a receipt. */
function receipt(at: Date): string {
  return `${readAtStamp(at)} (console receipt — the endpoint does not state when it read)`
}

/**
 * How many institutions the per-provider fan-outs will follow.
 *
 * The register is unpaginated BY DESIGN — it is "the set of institutions
 * somebody has integrated, tens not thousands" — so this bound is a backstop
 * rather than an expectation. It is stated on screen when it bites, because a
 * silently truncated matrix reads as a complete one.
 */
const MAX_FANOUT = 24

// ---------------------------------------------------------------------------
// The register, read once and shared by three screens
// ---------------------------------------------------------------------------

/** One institution, as much of it as this console could read. */
interface Institution {
  readonly key: string
  readonly legalName: string | null
  readonly legalCountry: string | null
  readonly regulatoryStatus: string | null
  readonly licenceRef: string | null
  readonly licenceAuthority: string | null
  readonly safeguards: boolean | null
  readonly adapterId: string | null
  readonly adapterClass: string | null
  readonly liveVersion: IntRead
  readonly dormant: boolean | null
  readonly healthStatus: string | null
  readonly healthObservedAt: InstantRead
  readonly liveCountries: readonly string[]
  readonly withdrawnEvidence: number
  readonly methods: readonly string[]
  readonly gaps: readonly string[]
}

/**
 * The health status vocabulary, and NOT ONE OF THEM IS RED — law 19.
 *
 * A degraded rail is a routing input and a stale verdict is stale reporting;
 * neither is an outage, and neither is a reason to block a money screen. The
 * corpus draws `degraded` in the critical tone, which is the exact reading the
 * law forbids. Red on this console means the console does not know — a read
 * that failed — which is a different claim from the institution being down, and
 * keeping it for that is what stops an operator learning to skip it.
 */
const HEALTH_TONE: Readonly<Record<string, Tone>> = {
  ok: 'g',
  degraded: 'a',
  unknown: 'a',
}

/**
 * `unknown` is three facts, and the remedy differs for each.
 *
 * Nothing was ever reported is a missing integration. Stale is a stopped
 * worker. Observed-ahead is a wrong clock. One word collapses all three, which
 * is why the backend carries a reason code beside it and why this console
 * renders the reason rather than the word.
 */
const HEALTH_REASON: Readonly<Record<string, string>> = {
  provider_health_unobserved: 'nothing has ever probed this institution',
  provider_health_stale: 'the last observation has passed its own expiry',
  provider_health_observed_ahead: 'the reporter’s clock is ahead of ours',
  provider_health_ok: 'the last observation is current and healthy',
  provider_health_degraded: 'the last observation reported it degraded',
}

function healthChip(status: string | null, gaps: string[], gap: string): ChipCell {
  if (status === null) {
    gaps.push(gap)
    return ['unstated', 'a']
  }
  if (!knows(HEALTH_TONE, status)) {
    gaps.push(`${gap} this console recognises`)
    return [status, 'a']
  }
  return [status, HEALTH_TONE[status] ?? 'a']
}

function readInstitution(raw: unknown): Institution | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const key = readText(o, 'provider_key')
  if (key === null) gaps.push('a provider key')

  // `live_version` is null where NOTHING GOVERNS, and the institution is still
  // listed. A register that hid such a key would send an operator to register
  // it again, which the primary key then refuses.
  const version = asRecord(o['live_version'])
  const legalName = version === null ? null : readText(version, 'legal_name')
  const legalCountry = version === null ? null : readText(version, 'legal_country')
  const regulatoryStatus = version === null ? null : readText(version, 'regulatory_status')
  const licenceRef = version === null ? null : readText(version, 'licence_ref')
  const licenceAuthority = version === null ? null : readText(version, 'licence_authority')
  const safeguards = version === null ? null : readBool(version, 'safeguards_customer_funds')
  const adapterId = version === null ? null : readText(version, 'adapter_id')
  const adapterClass = version === null ? null : readText(version, 'adapter_class')
  const liveVersion: IntRead = version === null
    ? { kind: 'absent' }
    : readInt(version, 'version')

  const health = asRecord(o['health'])
  const healthStatus = health === null ? null : readText(health, 'status')
  const healthObservedAt: InstantRead = health === null
    ? { kind: 'absent' }
    : readInstant(health, 'observed_at')

  const dormant = readBool(o, 'dormant')
  if (dormant === null) gaps.push('whether an institution is dormant')

  // Live activations are where the institution may be used TODAY. A country
  // absent from this list is one it may not be used in AT ALL — the fail-closed
  // reading — rather than one switched off.
  const countries: string[] = []
  let withdrawnEvidence = 0
  const activations = asArray(o['live_activations'])
  if (activations === null) {
    gaps.push('an activation list')
  } else {
    for (const entry of activations) {
      const a = asRecord(entry)
      if (a === null) { gaps.push('an activation this console could not read'); continue }
      const country = readText(a, 'country')
      if (country === null) { gaps.push('a country on an activation'); continue }
      countries.push(country)
      // The registration reference is the EVIDENCE the gate rests on. An
      // activation without it is unlicensed activity by the operator itself,
      // and a gate whose evidence no screen shows is one nobody can check.
      const registration = asRecord(a['registration'])
      if (registration === null || readText(registration, 'reference') === null) {
        withdrawnEvidence += 1
        gaps.push('the registration reference an activation rests on')
      }
    }
  }

  const methods: string[] = []
  const methodRows = asArray(o['methods'])
  if (methodRows === null) {
    gaps.push('the rails an institution carries')
  } else {
    for (const entry of methodRows) {
      const m = asRecord(entry)
      if (m === null) continue
      const code = readText(m, 'method_code')
      const direction = readText(m, 'direction')
      if (code === null) continue
      methods.push(direction === null ? code : `${code} ${direction}`)
    }
  }

  return {
    key: key ?? '',
    legalName, legalCountry, regulatoryStatus, licenceRef, licenceAuthority,
    safeguards, adapterId, adapterClass, liveVersion, dormant,
    healthStatus, healthObservedAt,
    liveCountries: countries, withdrawnEvidence, methods, gaps,
  }
}

function readRegister(payload: unknown): {
  readonly institutions: readonly Institution[]
  readonly unreadable: number
  readonly emptyNote: string | null
} | null {
  const o = asRecord(payload)
  if (o === null) return null
  const rows = asArray(o['providers'])
  if (rows === null) return null

  const institutions: Institution[] = []
  let unreadable = 0
  for (const raw of rows) {
    const one = readInstitution(raw)
    if (one === null || one.key === '') unreadable += 1
    else institutions.push(one)
  }
  return { institutions, unreadable, emptyNote: readText(o, 'empty_note') }
}

// ===========================================================================
// pv-registry
// ===========================================================================

/**
 * The columns.
 *
 * The corpus draws ID / Legal entity / Regulatory status / Adapter / Methods /
 * Health / Activation. The row id is dropped — the register is keyed by
 * `provider_key`, which is what every other surface names an institution by,
 * and a synthetic row number is one an operator would quote at somebody.
 *
 * `Activation` becomes COUNTRIES, because that is what an activation is: an
 * evidence-backed permission for ONE country. A single "active/blocked" chip
 * would flatten "live in DE and AT" and "live nowhere" into the same word.
 */
const REGISTER_COLS: readonly Column[] = [
  ['Provider', 'm', '140px'],
  ['Legal entity', 's', 'minmax(190px,1.4fr)'],
  ['Regulatory status', 'x', 'minmax(170px,1.2fr)'],
  ['Safeguards funds', 'c', '150px'],
  ['Adapter', 'm', '150px'],
  ['Rails', 'x', 'minmax(160px,1.2fr)'],
  ['Health', 'c', '124px'],
  ['Live in', 'x', 'minmax(150px,1.1fr)'],
]

function registerRow(i: Institution, gaps: string[]): readonly Cell[] {
  gaps.push(...i.gaps)

  // Both halves or neither. A legal entity with no country of incorporation is
  // half an identification, and the country here is where the institution is
  // INCORPORATED — not a market we are passported into. The two are unrelated.
  const entity: Cell = i.legalName === null
    ? ['— no live version', 'nothing governs this institution today']
    : [i.legalName, i.legalCountry === null ? '— country not stated' : i.legalCountry]

  // The licence reference and its authority travel together: a licence number
  // with no authority is not checkable, which is the only thing it is for.
  const status = i.regulatoryStatus === null
    ? '— not stated'
    : i.licenceRef === null || i.licenceAuthority === null
      ? i.regulatoryStatus
      : `${i.regulatoryStatus} · ${i.licenceRef} (${i.licenceAuthority})`

  // A fact about the INSTITUTION. Under EMD2 the licensed institution carries
  // the safeguarding obligation over customer money; we never hold and never
  // safeguard, so this must not read as a property of us.
  const safeguards: ChipCell = i.safeguards === null
    ? ['unstated', 'a']
    : i.safeguards ? ['the institution does', 'g'] : ['it does not', 'a']

  // `adapter_id` names WHICH adapter binds to the institution. It is not a
  // credential and stands for none — the adapter resolves its own secret at the
  // moment of the outbound call, and migration 029 stores none of it.
  const adapter = i.adapterId ?? (i.adapterClass ?? '— not stated')

  // An empty rail list means NO LIVE OFFERING names this institution, which is
  // the ordinary state of a newly registered one. The remedy is an offering,
  // not an amendment to the register.
  const rails = i.methods.length === 0 ? '— no live offering names it' : i.methods.join(', ')

  // Dormant is "no live activation in ANY country", said as a word because
  // "registered" reads as "live" to everyone who has not read the rule.
  const live = i.liveCountries.length === 0
    ? (i.dormant === true ? 'nowhere — dormant' : 'nowhere')
    : i.liveCountries.join(', ')

  return [
    i.key,
    entity,
    status,
    safeguards,
    adapter,
    rails,
    healthChip(i.healthStatus, gaps, 'a health status'),
    live,
  ]
}

/**
 * What the designed screen asks for that this read does not answer.
 *
 * The credential block is the one worth naming out loud: the corpus draws four
 * rotation dates and this backend stores no key, secret or certificate at all.
 */
const REGISTER_MISSING: readonly string[] = [
  'credential status and rotation dates — no key, secret or certificate is stored anywhere in this backend, so there is nothing to report the status of',
  'an activation control — no endpoint activates or withdraws an institution in a country, so none is drawn',
]

export function composeProviderRegistry(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Provider registry', 'The register could not be read',
      'No institution, licence or activation below is known — that is different from nothing being registered.',
      read, REGISTER_SOURCE,
    )
  }

  const register = readRegister(read.payload)
  if (register === null) {
    return unread(
      'Provider registry', 'The register could not be read',
      'The register returned a shape this console does not know.',
      {
        ok: false, reason: 'The register returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      REGISTER_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows = register.institutions.map((i) => registerRow(i, gaps))

  const dormant = register.institutions.filter((i) => i.liveCountries.length === 0).length

  // An empty register means no institution has been registered here — the
  // schema seeds none, deliberately, because a seeded provider row is a
  // regulatory assertion nobody signed for. A fresh environment answering `[]`
  // is easily read as a fault, so the backend's own sentence is preferred where
  // it sent one.
  const footer = rows.length === 0
    ? (register.unreadable === 0
        ? (register.emptyNote ?? 'No institution has been registered. Until one is registered and activated in a country, routing has no candidate.')
        : `No institution could be read: ${String(register.unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'institution' : 'institutions'}${dormant === 0 ? '' : ` · ${String(dormant)} live in no country at all`}${register.unreadable === 0 ? '' : ` · ${String(register.unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    t: 'table', title: 'Registered institutions', src: 'ledger', facets: false,
    cols: REGISTER_COLS, rows, footer,
  }]

  // The gate, stated where it bites. A country absent from an institution's
  // list is one it may NOT be used in — the fail-closed reading — and a screen
  // showing three countries is exactly where somebody concludes the other
  // twenty are merely switched off.
  if (rows.length > 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Activation',
      title: 'A country not listed is one this institution may not be used in',
      body: 'Activation is evidence-backed permission for one country, not a switch. The remedy for an absent country is a registration reference, not a toggle — and there is no control here that grants one.',
      ref: 'fail-closed · no activation endpoint exists',
    })
  }

  const missing = [...REGISTER_MISSING, ...summarise(gaps)]
  if (register.unreadable > 0) {
    missing.push(`${String(register.unreadable)} register row(s) this console could not read`)
  }

  return {
    blocks,
    provenance: {
      kind: 'partial', source: REGISTER_SOURCE, authority: REGISTER_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Provider registry',
    subject: `provider_registry · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys: register.institutions.map((i) => i.key),
  }
}

export const loadProviderRegistry: ScreenLoader = async (api) =>
  composeProviderRegistry(
    await attempt(() => api.get<unknown>(PROVIDERS_PATH), 'The provider register'),
    new Date(),
  )

// ===========================================================================
// pv-caps — the capability matrix
// ===========================================================================

/**
 * The nine capabilities, in the order the money path declares them, with what
 * each one decides.
 *
 * The sub-label is what the capability GOVERNS rather than a restatement of its
 * name, because the matrix is read by somebody deciding whether a product
 * decision is available — and `per_customer_iban` is the one invariant 1 turns
 * on, which no field name says.
 */
const CAPABILITIES: readonly (readonly [key: string, label: string, governs: string])[] = [
  ['per_customer_iban', 'PerCustomerIBAN', 'an IBAN in each customer’s own name'],
  ['swift_inbound', 'SWIFTInbound', 'credits arriving over SWIFT'],
  ['swift_outbound', 'SWIFTOutbound', 'payouts sent over SWIFT'],
  ['pushes_webhooks', 'PushesWebhooks', 'the institution calls us'],
  ['inbound_push', 'InboundPush', 'credits pushed as they land'],
  ['card_program', 'CardProgram', 'cards — Phase 2'],
  ['passes_debtor_iban', 'PassesDebtorIBAN', 'the payer’s IBAN on a credit'],
  ['passes_structured_remittance', 'PassesStructuredRemittance', 'ISO 20022 remittance data'],
  ['statement_pull_api', 'StatementPullAPI', 'statements we can fetch'],
]

/** One institution's answer, or why there is none. */
type CapabilityRead =
  | { readonly kind: 'read'; readonly caps: Record<string, unknown> }
  /** Registered here, but this deployment does not talk to it. A state, not a failure. */
  | { readonly kind: 'not_configured' }
  | { readonly kind: 'unread'; readonly reason: string }

/**
 * One cell of the matrix.
 *
 * `false` renders as **not confirmed** and never as a bare "no", because those
 * are different claims about a third party and the second is defamatory in a
 * compliance file if the institution does in fact offer it. Every capability
 * starts false and becomes true only on a written confirmation, so an operator
 * reading this column is reading OUR paperwork rather than their product.
 */
function capabilityCell(read: CapabilityRead, key: string, gaps: string[]): MtxCell {
  if (read.kind === 'not_configured') {
    return ['—', 'n', 'not integrated', 'this deployment does not talk to this institution', true,
      'Absent, not false: an institution we do not talk to has no capabilities to report.']
  }
  if (read.kind === 'unread') {
    return ['—', 'a', 'not read', read.reason, true,
      'This console could not read this institution’s capabilities.']
  }
  const raw = read.caps[key]
  if (typeof raw !== 'boolean') {
    gaps.push('a capability this console could not read')
    return ['—', 'a', 'not read', 'the field was not a boolean', true,
      'Absent rather than false: a value we could not read is not a confirmation we do not have.']
  }
  if (raw) {
    return ['true', 'g', 'confirmed', 'confirmed by the institution in writing']
  }
  // The whole point of the screen, and it is a `locked` cell so it reads as a
  // deliberate state rather than an empty one.
  return ['false', 'n', 'unconfirmed', 'not confirmed in writing', true,
    'False means NOT CONFIRMED IN WRITING — never that the institution cannot do this.']
}

export function composeProviderCapabilities(
  register: Attempt, perProvider: ReadonlyMap<string, Attempt>, at: Date,
): ScreenData {
  if (!register.ok) {
    return unread(
      'Capability matrix', 'The institutions could not be read',
      'Without the register there is nothing to ask about — and an empty matrix is not a matrix of falses.',
      register, CAPS_SOURCE,
    )
  }

  const read = readRegister(register.payload)
  if (read === null) {
    return unread(
      'Capability matrix', 'The institutions could not be read',
      'The register returned a shape this console does not know.',
      {
        ok: false, reason: 'The register returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      CAPS_SOURCE,
    )
  }

  const gaps: string[] = []
  const shown = read.institutions.slice(0, MAX_FANOUT)
  const truncated = read.institutions.length - shown.length

  const answers = new Map<string, CapabilityRead>()
  let notConfigured = 0
  let unreadable = 0
  for (const i of shown) {
    const a = perProvider.get(i.key)
    if (a === undefined || !a.ok) {
      // A 404 here is the backend saying "this deployment is not configured
      // with that institution", which is a STATE and the honest one: a row of
      // falses would read as a statement about our integration with it.
      if (a !== undefined && !a.ok && a.status === 404) {
        answers.set(i.key, { kind: 'not_configured' })
        notConfigured += 1
      } else {
        answers.set(i.key, {
          kind: 'unread',
          reason: a === undefined ? 'this console did not ask' : a.reason,
        })
        unreadable += 1
      }
      continue
    }
    const body = asRecord(a.payload)
    const caps = body === null ? null : asRecord(body['capabilities'])
    if (caps === null) {
      answers.set(i.key, { kind: 'unread', reason: 'the answer had no capability block' })
      unreadable += 1
      continue
    }
    answers.set(i.key, { kind: 'read', caps })
  }

  const rows: MtxRow[] = CAPABILITIES.map(([key, label, governs]) => [
    label,
    governs,
    shown.map((i) => capabilityCell(answers.get(i.key) ?? { kind: 'unread', reason: 'this console did not ask' }, key, gaps)),
  ])

  const matrix: MtxBlock = {
    t: 'mtx',
    title: 'emi.Capabilities',
    rowLabel: 'capability',
    cols: shown.map((i) => i.legalName ?? i.key),
    rows: shown.length === 0 ? [] : rows,
  }

  const blocks: Block[] = [matrix]

  // The backend's own sentence on what `false` means, where it sent one. Taken
  // from any institution's answer — the note is a property of the route rather
  // than of the institution, so the first one that read is as good as any.
  const firstRead = shown.map((i) => perProvider.get(i.key)).find((a) => a?.ok === true)
  const body = firstRead?.ok === true ? asRecord(firstRead.payload) : null
  if (body !== null) {
    const meaning = disclosure(body, 'meaning_note', 'Reading this', 'What false means here', 'a')
    if (meaning !== null) blocks.push(meaning)
    const change = disclosure(body, 'change_note', 'Changing one', 'How a capability becomes true', 'n')
    if (change !== null) blocks.push(change)
  }

  if (shown.length === 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Capability matrix',
      title: 'No institution is registered, so there is nothing to report on',
      body: 'An empty matrix is not a matrix of falses. Capabilities are a property of an institution we talk to, and none is registered here.',
      ref: 'the register answered with no institutions',
    })
  }

  const missing = [
    // Named because the corpus draws exactly this: an evidence column citing
    // "contract §4.2" and "observed on 12,408 credits".
    'the evidence behind a confirmation — a contract clause or an observation count is recorded on the issue that asked for it, not on this route',
    ...summarise(gaps),
  ]
  if (notConfigured > 0) {
    missing.push(`${String(notConfigured)} registered institution(s) this deployment does not talk to`)
  }
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} institution(s) whose capabilities could not be read`)
  }
  if (truncated > 0) {
    missing.push(`${String(truncated)} institution(s) beyond the ${String(MAX_FANOUT)} this screen asks about`)
  }

  return {
    blocks,
    provenance: {
      kind: 'partial', source: CAPS_SOURCE, authority: CAPS_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Capability matrix',
    subject: `emi.Capabilities · ${String(shown.length)} ${shown.length === 1 ? 'institution' : 'institutions'}`,
  }
}

/**
 * Reads the register, then each institution's capabilities.
 *
 * A fan-out, and a bounded one. The register is unpaginated by design because
 * it is tens of rows, so the cap is a backstop rather than an expectation — and
 * it is reported in the provenance when it bites, because a matrix silently
 * missing a column reads as a complete one.
 */
export const loadProviderCapabilities: ScreenLoader = async (api) => {
  const register = await attempt(() => api.get<unknown>(PROVIDERS_PATH), 'The provider register')
  const answers = new Map<string, Attempt>()
  if (register.ok) {
    const read = readRegister(register.payload)
    const keys = (read?.institutions ?? []).slice(0, MAX_FANOUT).map((i) => i.key)
    const results = await Promise.all(keys.map((key) =>
      attempt(() => api.get<unknown>(providerCapabilitiesPath(key)), `The capabilities of ${key}`)))
    keys.forEach((key, at) => {
      const result = results[at]
      if (result !== undefined) answers.set(key, result)
    })
  }
  return composeProviderCapabilities(register, answers, new Date())
}

// ===========================================================================
// pv-health — a routing input, never an outage
// ===========================================================================

/**
 * The columns, and there are three where the design drew eight.
 *
 * Gone, each refused by the backend by name: Method and Dir (the observations
 * table is keyed by `provider_key` ALONE, and attributing an institution's
 * outage to one rail is backwards for a routing decision), Success 1h (nothing
 * writes `error_rate` observations, so a computed rate renders `0% errors`
 * where the truth is "never measured"), p95 latency (a Prometheus gauge, not a
 * Postgres fact), and Routing effect (what routing DID is a different table and
 * a different fact — conflating them makes a routing bug look like an outage).
 *
 * What replaces them is what the verdict actually carries and the status column
 * alone cannot say: WHY it is what it is, whether anything has ever been
 * reported at all, and how long the reporter promised the answer would stand.
 */
const HEALTH_COLS: readonly Column[] = [
  ['Provider', 'm', '150px'],
  ['Status', 'c', '124px'],
  ['Why', 'x', 'minmax(230px,1.6fr)'],
  ['Ever observed', 'c', '140px'],
  ['Observations', 'n', '124px'],
  ['Last observed', 'm', '150px'],
  ['Reported by', 'x', '150px'],
  ['Verdict stands for', 'x', '150px'],
]

function healthRow(i: Institution, answer: Attempt | undefined, gaps: string[]): readonly Cell[] {
  if (answer === undefined || !answer.ok) {
    // Tone `a`, not `r`, even here: the institution is not down — this console
    // failed to ask. The distinction is stated in the cell rather than in a
    // colour, which is the channel an operator who cannot see colour still has.
    const reason = answer === undefined ? 'this console did not ask' : answer.reason
    gaps.push('a health verdict this console could not read')
    return [i.key, ['not read', 'a'], reason, ['—', 'a'], '—', '—', '—', '—']
  }
  const o = asRecord(answer.payload)
  if (o === null) {
    gaps.push('a health verdict this console could not read')
    return [i.key, ['not read', 'a'], 'the answer was not an object', ['—', 'a'], '—', '—', '—', '—']
  }

  const status = readText(o, 'status')
  const statusCell = healthChip(status, gaps, 'a health status')

  // The reason code is the column, not the status. `unknown` is three facts
  // with three remedies and the word collapses all of them.
  const reasonCode = readText(o, 'reason_code')
  if (reasonCode === null) gaps.push('the reason a health verdict is what it is')
  const why = reasonCode === null
    ? '— no reason stated'
    : knows(HEALTH_REASON, reasonCode)
      ? `${HEALTH_REASON[reasonCode] ?? reasonCode} · ${reasonCode}`
      : reasonCode

  // Taken from a COUNT rather than from the reason code, because an unparseable
  // cached status also resolves to `unobserved` on a row that has been
  // observed. The two disagreeing is itself worth seeing.
  const observed = readBool(o, 'observed')
  if (observed === null) gaps.push('whether an institution has ever been observed')
  const observedCell: ChipCell = observed === null
    ? ['unstated', 'a']
    : observed ? ['yes', 'g'] : ['never', 'a']

  const count = readInt(o, 'observation_count')
  if (count.kind !== 'exact') gaps.push('an observation count')

  const observedAt = readInstant(o, 'observed_at')
  const reportedBy = readText(o, 'reported_by')
  const source = readText(o, 'source')

  // A zero expiry is a FINDING, not a default: a verdict with no expiry is not
  // a verdict that never expires, and rendering it as a blank would hide the
  // reporter that promised nothing.
  const stale = readInt(o, 'stale_after_seconds')
  if (stale.kind !== 'exact') gaps.push('the expiry a reporter promised')
  const staleCell = stale.kind !== 'exact'
    ? '—'
    : stale.value === 0
      ? '— no expiry promised'
      : spanOf(stale.value)

  return [
    i.key,
    statusCell,
    why,
    observedCell,
    count.kind === 'exact' ? String(count.value) : '—',
    observedAt.kind === 'at' ? observedAt.label : observedAt.kind === 'absent' ? 'never' : '—',
    reportedBy === null
      ? (source === null ? '— not stated' : source)
      : (source === null ? reportedBy : `${reportedBy} · ${source}`),
    staleCell,
  ]
}

const HEALTH_MISSING: readonly string[] = [
  'per-rail posture — health is observed per institution and the schema cannot hold a per-method or per-direction verdict; attributing one institution’s outage to a single rail is backwards for a routing decision',
  'a success rate — it needs error-rate observations and nothing in this build writes one, so a computed rate would render 0% errors where the truth is “never measured”',
  'p95 latency — there is no latency figure in this database at all',
  'the routing effect of a verdict — what routing actually did is recorded per candidate and is a different fact from what a probe said',
]

export function composeProviderHealth(
  register: Attempt, perProvider: ReadonlyMap<string, Attempt>, at: Date,
): ScreenData {
  if (!register.ok) {
    return unread(
      'Provider health', 'The institutions could not be read',
      'Nothing below is known. That is NOT an outage — it is this console failing to ask.',
      register, HEALTH_SOURCE,
    )
  }

  const read = readRegister(register.payload)
  if (read === null) {
    return unread(
      'Provider health', 'The institutions could not be read',
      'The register returned a shape this console does not know.',
      {
        ok: false, reason: 'The register returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      HEALTH_SOURCE,
    )
  }

  const gaps: string[] = []
  const shown = read.institutions.slice(0, MAX_FANOUT)
  const truncated = read.institutions.length - shown.length
  const rows = shown.map((i) => healthRow(i, perProvider.get(i.key), gaps))

  let degraded = 0
  let neverObserved = 0
  for (const i of shown) {
    const a = perProvider.get(i.key)
    if (a === undefined || !a.ok) continue
    const o = asRecord(a.payload)
    if (o === null) continue
    if (readText(o, 'status') === 'degraded') degraded += 1
    if (readBool(o, 'observed') === false) neverObserved += 1
  }

  // The design's empty state is right and worth keeping: no health data means
  // the checker is not running, which is itself the finding.
  const footer = rows.length === 0
    ? 'No institution is registered, so there is nothing to have a health verdict about.'
    : `${String(rows.length)} ${rows.length === 1 ? 'institution' : 'institutions'}${degraded === 0 ? '' : ` · ${String(degraded)} degraded, which excludes a candidate from routing rather than blocking anything`}${neverObserved === 0 ? '' : ` · ${String(neverObserved)} never probed at all`}`

  const blocks: Block[] = [{
    t: 'table',
    // `provider` as the source, which the corpus gets right: the verdict is
    // about a third party, observed by us. Not the analytics mirror — there is
    // no ClickHouse behind any figure here, so no analytics badge and no mirror
    // freshness stamp (law 12). The freshness that exists is each observation's
    // own age, in its own column.
    title: 'Health verdicts', src: 'provider', facets: false,
    cols: HEALTH_COLS, rows, footer,
  }]

  // Law 19, said once and briefly. A degraded rail is a routing input; it is
  // not an outage and it does not block a money screen. Said on the screen
  // where somebody would otherwise reach for a red banner.
  if (rows.length > 0) {
    blocks.push({
      t: 'note', tone: 'n', quiet: true,
      kicker: 'Reading this',
      title: 'Degraded is a routing input, not an outage',
      body: 'A degraded institution is skipped as a routing candidate. It does not block a money screen, it is not a service-down state, and nothing on this screen is a reason to stop work.',
      ref: 'law 19 · health is observed, never asserted',
    })
  }

  // The one genuinely alarming state on this screen, and it is not "degraded":
  // an institution nobody has ever probed means the checker is not running.
  if (neverObserved > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Checker',
      title: `${String(neverObserved)} institution${neverObserved === 1 ? ' has' : 's have'} never been probed`,
      body: 'No observation has ever been recorded against them. That is a missing integration or a worker that has never run — not an institution that is unwell.',
      ref: 'observed = false',
    })
  }

  const missing = [...HEALTH_MISSING, ...summarise(gaps)]
  if (truncated > 0) {
    missing.push(`${String(truncated)} institution(s) beyond the ${String(MAX_FANOUT)} this screen asks about`)
  }

  return {
    blocks,
    provenance: {
      kind: 'partial', source: HEALTH_SOURCE, authority: HEALTH_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Provider health',
    subject: `provider_health · ${String(rows.length)} ${rows.length === 1 ? 'institution' : 'institutions'}`,
    rowKeys: shown.map((i) => i.key),
  }
}

export const loadProviderHealth: ScreenLoader = async (api) => {
  const register = await attempt(() => api.get<unknown>(PROVIDERS_PATH), 'The provider register')
  const answers = new Map<string, Attempt>()
  if (register.ok) {
    const read = readRegister(register.payload)
    const keys = (read?.institutions ?? []).slice(0, MAX_FANOUT).map((i) => i.key)
    const results = await Promise.all(keys.map((key) =>
      attempt(() => api.get<unknown>(providerHealthPath(key)), `The health of ${key}`)))
    keys.forEach((key, at) => {
      const result = results[at]
      if (result !== undefined) answers.set(key, result)
    })
  }
  return composeProviderHealth(register, answers, new Date())
}

// ---------------------------------------------------------------------------
// One institution's probe history — the row opener
// ---------------------------------------------------------------------------

/**
 * What the verdict cannot answer.
 *
 * An institution reading `unknown` because its verdict went stale is one of two
 * entirely different situations: the heartbeats STOPPED ARRIVING, which is our
 * worker, or they kept arriving and started saying `probe_unreachable`, which
 * is the institution. Both render identically on a screen showing only the
 * verdict, and reading the gap in this history is how an operator tells them
 * apart.
 */
const OBSERVATION_COLS: readonly Column[] = [
  ['Observed', 'm', '150px'],
  ['Status', 'c', '116px'],
  ['Reason', 'm', '190px'],
  ['Source', 'x', '130px'],
  ['Reported by', 'x', '150px'],
  ['Stood for', 'x', '124px'],
  ['Detail', 'x', 'minmax(180px,1.4fr)'],
]

export function composeProviderObservations(read: Attempt, key: string, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Probe history', 'The probe history could not be read',
      'Nothing below is known — which is not the same as nothing having probed this institution.',
      read, HEALTH_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['observations'])
  if (o === null || list === null) {
    return unread(
      'Probe history', 'The probe history could not be read',
      'The history returned a shape this console does not know.',
      {
        ok: false, reason: 'The history returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      HEALTH_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  for (const raw of list) {
    const e = asRecord(raw)
    if (e === null) { unreadable += 1; continue }

    const observedAt = readInstant(e, 'observed_at')
    if (observedAt.kind !== 'at') gaps.push('when an observation was made')
    const status = readText(e, 'status')
    const reason = readText(e, 'reason_code')
    const source = readText(e, 'source')
    if (source === null) gaps.push('the source of an observation')
    const reportedBy = readText(e, 'reported_by')
    const stale = readInt(e, 'stale_after_seconds')
    const detail = readText(e, 'detail')

    rows.push([
      observedAt.kind === 'at' ? observedAt.label : '—',
      healthChip(status, gaps, 'an observation status'),
      reason ?? '— none',
      source ?? '— not stated',
      reportedBy ?? '— not stated',
      stale.kind === 'exact' ? (stale.value === 0 ? '— no expiry' : spanOf(stale.value)) : '—',
      detail ?? '',
    ])
  }

  const hasMore = readBool(o, 'has_more') === true

  const blocks: Block[] = [{
    t: 'table', title: 'Observations', src: 'provider', facets: false,
    cols: OBSERVATION_COLS, rows,
    footer: rows.length === 0
      ? (unreadable === 0
          ? 'Nothing has ever probed this institution. The history is append-only, so an empty one means no observation was ever recorded — not that they were cleared.'
          : `No observation could be read: ${String(unreadable)} came back in a shape this console does not know.`)
      : `${String(rows.length)} ${rows.length === 1 ? 'observation' : 'observations'} · newest first, append-only${hasMore ? ' · the backend caps the page' : ''}`,
  }]

  const missing = [
    'the attempt and failure counts an error-rate observation would carry — nothing in this build writes one, so they are absent on every row in every deployment',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} observation(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: HEALTH_SOURCE, authority: HEALTH_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: `Probe history · ${key}`,
    subject: `provider_health_observations · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

/** One institution's probe history, opened from a health row. */
export async function readProviderObservations(api: ApiClient, key: string): Promise<ScreenData> {
  return composeProviderObservations(
    await attempt(() => api.get<unknown>(providerObservationsPath(key)), `The probe history of ${key}`),
    key,
    new Date(),
  )
}

// ===========================================================================
// pv-invoices — an unreconciled invoice has no delta, and its delta is not zero
// ===========================================================================

/**
 * The columns.
 *
 * The corpus draws Period / Provider / Our accrual / Invoiced / Delta / Case /
 * State, with `2026-06` as the period. The period is HALF-OPEN `[start, end)`
 * and deliberately not constrained to a calendar month — a provider billing
 * 26th-to-25th would otherwise be rendered as a month it is not — so the column
 * carries the actual range.
 *
 * `Reference` is added because a delta is only arguable if we can name the
 * document it came from, and `Uncosted` is added because it is what decides
 * whether a matching total is agreement or a coincidence.
 */
const INVOICE_COLS: readonly Column[] = [
  ['Period', 'm', '190px'],
  ['Provider', 'x', '120px'],
  ['Invoice', 'm', '150px'],
  ['Invoiced', 'a', '150px'],
  ['Our accrual', 'a', '150px'],
  ['Delta', 'a', '150px'],
  ['Uncosted', 'n', '110px'],
  ['Outcome', 'c', '150px'],
  ['Case', 'm', '110px'],
  ['Standing', 'c', '150px'],
]

/**
 * The outcome vocabulary. Branch on THIS, never on the delta.
 *
 * A comparison whose money and counts both match is still `discrepant` while
 * any settled payment in the period carries no pinned cost: an incomplete
 * accrual is a coincidence rather than a tie, and the schema refuses to record
 * it as one.
 */
const OUTCOME_TONE: Readonly<Record<string, Tone>> = { agreed: 'g', discrepant: 'a' }

function invoiceRow(raw: unknown, gaps: string[]): {
  readonly cells: readonly Cell[]
  readonly open: boolean
  readonly unreconciled: boolean
} | null {
  const o = asRecord(raw)
  if (o === null) return null

  const provider = readText(o, 'provider')
  if (provider === null) gaps.push('a provider on an invoice')
  const reference = readText(o, 'reference')
  if (reference === null) gaps.push('an invoice reference')

  const from = dayOf(o['period_start'])
  const to = dayOf(o['period_end'])
  if (from === null || to === null) gaps.push('a billing period')
  // Half-open, and rendered as such rather than as a month: `2026-06` over a
  // 26th-to-25th period is a month the invoice is not for.
  const period = from === null || to === null ? '— not stated' : `${from} → ${to}`

  if (!moneyReadable(o, 'total')) gaps.push('an invoiced total')

  // THE CELL THIS SCREEN TURNS ON. `reconciled: false` means NOBODY HAS RUN THE
  // COMPARISON and the whole reconciliation block is absent. A zero in the
  // delta column would report "this period tied to the cent" about a document
  // nobody has looked at.
  const reconciled = readBool(o, 'reconciled')
  if (reconciled === null) gaps.push('whether an invoice has been reconciled')
  const recon = asRecord(o['reconciliation'])

  if (reconciled !== true || recon === null) {
    const standing: ChipCell = reconciled === null
      ? ['unstated', 'a']
      : ['not compared yet', 'n']
    return {
      cells: [
        period,
        provider ?? '—',
        reference ?? '— none',
        moneyCell(o, 'total'),
        // Absent, never zero. Three cells that do not exist rather than three
        // that claim a tie.
        '— no comparison has been run',
        '— no comparison has been run',
        '—',
        ['not compared', 'n'],
        '—',
        standing,
      ],
      open: false,
      unreconciled: true,
    }
  }

  if (!moneyReadable(recon, 'accrued')) gaps.push('an accrued figure')
  if (!moneyReadable(recon, 'delta')) gaps.push('a delta figure')

  const uncosted = readInt(recon, 'uncosted_count')
  if (uncosted.kind !== 'exact') gaps.push('an uncosted-payment count')

  const outcome = readText(recon, 'outcome')
  if (outcome === null) gaps.push('a reconciliation outcome')
  const outcomeCell: ChipCell = outcome === null
    ? ['unstated', 'a']
    : knows(OUTCOME_TONE, outcome)
      ? [outcome, OUTCOME_TONE[outcome] ?? 'a']
      : [outcome, 'a']

  const caseRef = readText(recon, 'case_ref')

  // `open` is the domain's answer, not a predicate re-derived here. A case
  // leaves the open set by being posted or resolved — NEVER by being
  // acknowledged, which is the distinction a console deriving its own "is this
  // done" would lose first.
  const open = readBool(recon, 'open')
  if (open === null) gaps.push('whether a cost case is still open')
  const acknowledged = readText(recon, 'acknowledged_by')
  const resolution = readText(recon, 'resolution')

  const standing: ChipCell = open === null
    ? ['unstated', 'a']
    : open
      ? [acknowledged === null ? 'open' : 'open · seen, not settled', 'a']
      : [resolution === null ? 'closed' : `closed · ${resolution}`, 'g']

  return {
    cells: [
      period,
      provider ?? '—',
      reference ?? '— none',
      moneyCell(o, 'total'),
      moneyCell(recon, 'accrued'),
      moneyCell(recon, 'delta'),
      uncosted.kind === 'exact' ? String(uncosted.value) : '—',
      outcomeCell,
      caseRef ?? '—',
      standing,
    ],
    open: open === true,
    unreconciled: false,
  }
}

const INVOICE_MISSING: readonly string[] = [
  'a clearing posting — no difference on this screen has ever been cleared by one, and the field is absent on every row by construction; the designed “the delta posts to a cost-discrepancy account” describes unbuilt work',
  'a total of open deltas — a sum of differences nobody has posted is a figure to argue, never money that moved, and beside real amounts it would read as some',
]

export function composeProviderInvoices(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Provider invoice reconciliation', 'The invoices could not be read',
      'No document, accrual or delta below is known — that is different from there being nothing to reconcile.',
      read, INVOICES_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['provider_invoices'])
  if (o === null || list === null) {
    return unread(
      'Provider invoice reconciliation', 'The invoices could not be read',
      'The register returned a shape this console does not know.',
      {
        ok: false, reason: 'The register returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      INVOICES_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let open = 0
  let unreconciled = 0
  for (const raw of list) {
    const row = invoiceRow(raw, gaps)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    if (row.open) open += 1
    if (row.unreconciled) unreconciled += 1
  }

  const hasMore = readBool(o, 'has_more') === true

  // The design's empty state is right and kept: an invoice arrives monthly in
  // arrears, so the current period having none is expected rather than a gap.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No invoice has been recorded. Invoices arrive in arrears, so a period with none is expected rather than missing.'
        : `No invoice could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'document' : 'documents'}${open === 0 ? '' : ` · ${String(open)} cost ${open === 1 ? 'case' : 'cases'} still open`}${unreconciled === 0 ? '' : ` · ${String(unreconciled)} not compared yet`}${hasMore ? ' · the backend caps the page' : ''}`

  const blocks: Block[] = [{
    t: 'table', title: 'Provider invoices', src: 'ledger', facets: false,
    cols: INVOICE_COLS, rows, footer,
  }]

  // The backend's own sentence on the two ways to report a tie that is not one.
  const reconciliation = disclosure(
    o, 'reconciliation_note', 'Reading a delta', 'When a delta is not a delta', 'a',
  )
  if (reconciliation !== null && rows.length > 0) blocks.push(reconciliation)

  const posting = disclosure(
    o, 'posting_note', 'Clearing', 'No difference here has ever been cleared', 'n',
  )
  if (posting !== null && open > 0) blocks.push(posting)

  const missing = [...INVOICE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} invoice row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: INVOICES_SOURCE, authority: INVOICE_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Provider invoice reconciliation',
    subject: `provider_invoices · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadProviderInvoices: ScreenLoader = async (api) =>
  composeProviderInvoices(
    await attempt(() => api.get<unknown>(PROVIDER_INVOICES_PATH), 'The provider invoices'),
    new Date(),
  )

// ===========================================================================
// pv-methods — the rail catalogue, which drives form generation everywhere else
// ===========================================================================

/**
 * The columns.
 *
 * `Settlement` (`T+1`, `instant`, `T+2`) is DROPPED: no such field exists on
 * this wire, and a settlement cycle invented on a catalogue screen is one an
 * operator would quote to a customer.
 *
 * `Version` and `Versions` are two facts and both are drawn: the NEWEST version
 * is not the live one, and on a retired rail the newest is closed and nothing
 * governs. `Live` is read rather than derived from `effective_to` being null,
 * which is what a client eventually gets wrong.
 */
const METHOD_COLS: readonly Column[] = [
  ['Rail', 'm', '150px'],
  ['Name', 'x', '160px'],
  ['Direction', 'c', '110px'],
  ['Live', 'c', '110px'],
  ['Required of the beneficiary', 'x', 'minmax(220px,1.6fr)'],
  ['Required of the sender', 'x', 'minmax(180px,1.2fr)'],
  ['Version', 'n', '96px'],
  ['Versions', 'n', '100px'],
  ['Effective from', 'm', '150px'],
]

/**
 * One party's field list, in the order the form asks for them.
 *
 * `known: false` is the FINDING and is marked in place: it is a field kind this
 * build has no validator for, which is what explains a rail that refuses a
 * payment. Optional fields are marked too — a form that asked for every field
 * as required would be a different form.
 */
function fieldList(
  fields: readonly unknown[], party: string, gaps: string[],
): { readonly label: string; readonly unknownFields: number } {
  const named: { position: number; label: string }[] = []
  let unknownFields = 0
  for (const raw of fields) {
    const f = asRecord(raw)
    if (f === null) { gaps.push('a required field this console could not read'); continue }
    if (readText(f, 'party') !== party) continue
    const name = readText(f, 'field')
    if (name === null) { gaps.push('a field name'); continue }
    const known = readBool(f, 'known')
    if (known === null) gaps.push('whether a field kind is one this build knows')
    const required = readBool(f, 'required')
    const position = readInt(f, 'position')
    let label = name
    if (required === false) label = `${label} (optional)`
    if (known === false) { label = `${label} — no validator in this build`; unknownFields += 1 }
    named.push({ position: position.kind === 'exact' ? position.value : Number.MAX_SAFE_INTEGER, label })
  }
  named.sort((a, b) => a.position - b.position)
  return {
    label: named.length === 0 ? '— none declared' : named.map((n) => n.label).join(', '),
    unknownFields,
  }
}

const METHOD_MISSING: readonly string[] = [
  'a settlement cycle — no field on this read carries one, and a T+1 or “instant” invented here is a promise an operator would repeat to a customer',
]

export function composeMethodCatalogue(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Method catalog', 'The rail catalogue could not be read',
      'No rail below is known — that is different from no method being registered, and with no method there is nothing to route.',
      read, METHODS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['payment_methods'])
  if (o === null || list === null) {
    return unread(
      'Method catalog', 'The rail catalogue could not be read',
      'The catalogue returned a shape this console does not know.',
      {
        ok: false, reason: 'The catalogue returned a shape this console does not know.',
        decisionRef: null, refused: false, status: null,
      },
      METHODS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let live = 0
  let unknownFields = 0

  for (const raw of list) {
    const m = asRecord(raw)
    if (m === null) { unreadable += 1; continue }

    const code = readText(m, 'method_code')
    if (code === null) gaps.push('a rail code')
    const name = readText(m, 'display_name')
    const direction = readText(m, 'direction')
    if (direction === null) gaps.push('a direction')

    const isLive = readBool(m, 'live')
    if (isLive === null) gaps.push('whether a rail is live')
    if (isLive === true) live += 1

    const version = readInt(m, 'version')
    if (version.kind !== 'exact') gaps.push('a rail version')
    const versions = readInt(m, 'versions')

    const fields = asArray(m['fields'])
    if (fields === null) gaps.push('the fields a rail requires')
    const beneficiary = fields === null
      ? { label: '— not read', unknownFields: 0 }
      : fieldList(fields, 'beneficiary', gaps)
    const sender = fields === null
      ? { label: '— not read', unknownFields: 0 }
      : fieldList(fields, 'sender', gaps)
    unknownFields += beneficiary.unknownFields + sender.unknownFields

    const from = readInstant(m, 'effective_from')
    if (from.kind !== 'at') gaps.push('an effective-from date')

    rows.push([
      code ?? '— unnamed',
      name ?? '—',
      direction === null ? ['unstated', 'a'] : [direction, 'n'],
      // A retired rail is neutral rather than critical: nothing is wrong with a
      // rail somebody closed on purpose, and routing simply has no candidate.
      isLive === null ? ['unstated', 'a'] : isLive ? ['live', 'g'] : ['retired', 'n'],
      beneficiary.label,
      sender.label,
      version.kind === 'exact' ? String(version.value) : '—',
      versions.kind === 'exact' ? String(versions.value) : '—',
      from.kind === 'at' ? from.label : '—',
    ])
  }

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No rail is registered. Without a method there is nothing to route and no beneficiary form to generate.'
        : `No rail could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'rail' : 'rails'} · ${String(live)} live · every rail is listed, retired ones included${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    t: 'table', title: 'Rail catalogue', src: 'ledger', facets: false,
    cols: METHOD_COLS, rows, footer,
  }]

  // The finding an operator acts on: a field kind with no validator is why a
  // rail refuses a payment, and it is invisible in the field list otherwise.
  if (unknownFields > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Fields',
      title: `${String(unknownFields)} declared field${unknownFields === 1 ? ' has' : 's have'} no validator in this build`,
      body: 'A rail declaring a field kind this build does not know will refuse the payment that supplies it. The field is marked in place above.',
      ref: 'known = false',
    })
  }

  const missing = [...METHOD_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} rail(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: METHODS_SOURCE, authority: 'operational',
      readAt: receipt(at), missing,
    },
    title: 'Method catalog',
    subject: `payment_methods · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadMethodCatalogue: ScreenLoader = async (api) =>
  composeMethodCatalogue(
    await attempt(() => api.get<unknown>(PAYMENT_METHODS_PATH), 'The rail catalogue'),
    new Date(),
  )
